import { isAxiosError } from 'axios'
import { Alert, Box, Stack, Text, Group, ThemeIcon, PasswordInput, Button } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconLogout, IconServer } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { profileApi } from '@/api/profile'
import { handleLogout } from '@/auth/handleLogout'
import { Callout, WidgetCard } from '@/components/ui'
import { useAcessoServidores, useInvalidarAcessoServidores } from '@/hooks/useAcessoServidores'
import { interpretarErroDeTrocaDeSenha } from '@/pages/auth/changePasswordOutcome'
import { CampoNovaSenha } from './CampoNovaSenha'

export const ID_SENHA_UNIX = 'senha-unix'

/** O Kanidm recusa com `tooshort` abaixo disto (`kanidm_password_errors.py`). */
const MINIMO_SENHA_UNIX = 10

function formatarDataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

function detalheDoErro(err: unknown): string | undefined {
  if (!isAxiosError(err)) return undefined
  const data: unknown = err.response?.data
  if (data && typeof data === 'object' && 'detail' in data && typeof data.detail === 'string') {
    return data.detail
  }
  return undefined
}

/**
 * A senha que o `sudo` pede nos servidores abertos pelo ConsoleAI (credencial UNIX do
 * Kanidm). O login do ConsoleAI e OIDC e usa a senha primaria, entao quem nunca
 * definiu esta nao sabe qual e ela — e nao havia onde defini-la sem o admin.
 *
 * So aparece para quem abre shell pessoal em algum tenant (ou ja tem a senha
 * registrada): para o resto e um formulario sem uso.
 */
export function SenhaUnixCard() {
  const { data } = useAcessoServidores()
  const invalidar = useInvalidarAcessoServidores()
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pedeLoginRecente, setPedeLoginRecente] = useState(false)

  const visivel = !!data && (data.tenants.length > 0 || !!data.senha_unix_definida_em)

  // `/perfil#senha-unix` (banner e alerta de Servidores): o roteador nao rola ate a
  // ancora, e o cartao so existe depois que a consulta responde.
  useEffect(() => {
    if (visivel && window.location.hash === `#${ID_SENHA_UNIX}`) {
      document.getElementById(ID_SENHA_UNIX)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [visivel])

  const mismatch = confirmPw.length > 0 && confirmPw !== newPw

  const mutation = useMutation({
    mutationFn: () => profileApi.definirSenhaUnix(newPw),
    onSuccess: () => {
      setNewPw(''); setConfirmPw(''); setPedeLoginRecente(false)
      void invalidar()
      notifications.show({
        color: 'teal',
        title: 'Senha UNIX definida',
        message: 'O sudo dos servidores passa a pedir esta senha. A senha do login não mudou.',
      })
    },
    onError: (err: unknown) => {
      const detail = detalheDoErro(err)
      // Sessao com mais de 15 min: a troca libera sudo, entao um token roubado nao
      // pode bastar. O aviso fica no cartao, com o botao, em vez de sumir no toast.
      if (detail === 'reautenticacao_necessaria') { setPedeLoginRecente(true); return }
      // A rota classifica a falha do Kanidm como a troca de senha do login
      // (`classificar_falha_de_credencial`): a mesma traducao serve, e uma segunda
      // copia das frases divergiria. `ja_trocada` nao sai desta rota.
      const resultado = interpretarErroDeTrocaDeSenha(detail)
      const msg = resultado.tipo === 'erro' ? resultado.mensagem : 'Erro ao definir a senha UNIX.'
      notifications.show({ color: 'red', title: 'Erro', message: msg })
    },
  })

  if (!visivel) return null

  const canSubmit = newPw.length >= MINIMO_SENHA_UNIX && newPw === confirmPw

  return (
    // A ancora mora num Box: `WidgetCard` nao repassa atributo HTML (`id`) no tipo.
    <Box id={ID_SENHA_UNIX} style={{ scrollMarginTop: 16 }}>
      <WidgetCard p="md">
        <Stack gap="sm">
          <Group gap="sm" align="center">
            <ThemeIcon color="violet" variant="light" size="md" radius="md">
              <IconServer size={16} />
            </ThemeIcon>
            <Text fw={600}>Senha UNIX (servidores)</Text>
          </Group>

          <Callout variant="info" title="Senha do sudo">
            É a senha que o sudo pede nos servidores abertos pelo ConsoleAI, com o seu usuário
            do LDAP. Definir aqui não muda a senha que você usa para entrar no ConsoleAI.
          </Callout>

          <Text size="xs" c="dimmed">
            {data.senha_unix_definida_em
              ? `Definida pelo ConsoleAI em ${formatarDataHora(data.senha_unix_definida_em)}.`
              : 'O ConsoleAI não tem registro de que você já definiu esta senha.'}
          </Text>

          {pedeLoginRecente && (
            <Alert color="yellow" title="Login recente necessário">
              <Text size="sm" mb="xs">
                Por segurança, esta troca exige login recente. Saia e entre de novo no ConsoleAI e repita.
              </Text>
              <Button
                size="xs" variant="light" color="yellow"
                leftSection={<IconLogout size={14} />}
                onClick={() => { void handleLogout() }}
              >
                Sair e entrar de novo
              </Button>
            </Alert>
          )}

          <CampoNovaSenha
            value={newPw}
            onChange={setNewPw}
            onGerada={() => setConfirmPw('')}
            minimo={MINIMO_SENHA_UNIX}
          />

          <PasswordInput
            label="Confirmar nova senha"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.currentTarget.value)}
            placeholder="Repita a nova senha"
            error={mismatch ? 'As senhas não coincidem' : undefined}
            data-sensitive="true"
            wrapperProps={{ 'data-sensitive': 'true' }}
          />

          <Group justify="flex-end">
            <Button
              size="sm" color="violet"
              type="button"
              loading={mutation.isPending}
              disabled={!canSubmit}
              onClick={() => mutation.mutate()}
            >
              Definir senha UNIX
            </Button>
          </Group>
        </Stack>
      </WidgetCard>
    </Box>
  )
}
